import { useMemo, useRef } from 'react';

/** What `useOpenRequest` hands back. Stable for the life of the component. */
export interface OpenRequest {
  /** Start an open and get its token. Retires any open still in flight. */
  begin: () => number;
  /** Is this token still the one the user is waiting for? Check after every await. */
  isCurrent: (token: number) => boolean;
  /** Retire whatever is in flight — call it when the overlay is closed. */
  cancel: () => void;
}

/**
 * Keeps a slow asynchronous *open* from winning a race against the close that came after it.
 *
 * Opening the contact overlay means fetching a venue and its contacts first, and a contact
 * document carries its mail bodies — about a megabyte each — so on a throttled connection those
 * requests take seconds. In that window the user can press Escape, or click another row. Without
 * a guard the request that lands late still runs its `setIsContactOverlayOpen(true)`, and the
 * overlay reappears on its own holding the row the user had already dismissed — or the venue it
 * was opened on gets another venue's contacts poured into it.
 *
 * Each open calls `begin()` for a token and checks `isCurrent(token)` after every await before
 * touching state; closing calls `cancel()`. The object identity never changes, so callers can
 * put it in a dependency array without rebuilding their callbacks on every render.
 */
export function useOpenRequest(): OpenRequest {
  const latest = useRef(0);
  return useMemo(
    () => ({
      begin: () => (latest.current += 1),
      isCurrent: (token: number) => latest.current === token,
      cancel: () => {
        latest.current += 1;
      },
    }),
    [],
  );
}
